import { authHeaders, del, get, post, put } from "../../../utils/fetch";
import { Hosts, RequestState } from "../../../constants";

// Actions
export const FETCH_CHATBOT_TOOLS = 'FETCH_CHATBOT_TOOLS';
export const CREATE_CHATBOT_TOOL = 'CREATE_CHATBOT_TOOL';
export const UPDATE_CHATBOT_TOOL = 'UPDATE_CHATBOT_TOOL';
export const DELETE_CHATBOT_TOOL = 'DELETE_CHATBOT_TOOL';
export const FETCH_CHATBOT_TOOL = 'FETCH_CHATBOT_TOOL';

// Add selectors at the top level of the file
export const selectChatbotTools = (state, chatbotId) => {
    return state?.models?.chatbotTools?.[chatbotId] || [];
};

// Base description for the escalation tool
export const ESCALATION_TOOL_BASE_DESCRIPTION = 'Escalate customer to human support. Ask for their name, valid email, and issue details. This will collect customer information and check the realtime support status : the result of the API call will be {status: "success", body: {status: status}} with status having the value "online", "offline" or "unknown". If status is "online", tell user an agent will be talking with them soon. If status is "offline", tell user an agent will contact them by email. If status is "unknown", tell user an agent can answer by chat or email.';

// Helper function to create TalkJS agent escalation tool body
export const createAgentEscalationToolBody = (projectId, chatbotId) => {
    
    return {
        project_id: projectId,
        chatbot_id: chatbotId,
        name: 'Escalate to Human Agent',
        description: ESCALATION_TOOL_BASE_DESCRIPTION,
        tool_type: 'api',
        enabled: true,
        config: {
            endpoint_template: `${Hosts.EDGE_CLOUD_CONTROLLER_API}/chatbot/${chatbotId}/support_status?project_id=${projectId}`,
            method: 'GET'
        },
        parameters: {
            type: 'object',
            properties: {
                customer_name: {
                    type: 'string',
                    description: 'Customer full name'
                },
                customer_email: {
                    type: 'string',
                    description: 'Customer email address'
                },
                issue_description: {
                    type: 'string',
                    description: 'Description of the customer issue or question'
                },
                timestamp: {
                    type: 'string',
                    description: 'Current timestamp in ISO format'
                }
            },
            required: ['customer_name', 'customer_email', 'issue_description', 'timestamp']
        }
    };
};

const ChatbotTools = {
    actions: {
        // List tools
        getChatbotTools: (projectId, chatbotId, skip = 0, limit = 100) => async (dispatch) => {
            let result = await get({
                host: Hosts.EDGE_CLOUD_CONTROLLER_API,
                url: `/chatbot/${chatbotId}/tools`,
                params: { project_id: projectId, skip, limit },
                headers: authHeaders(),
                action: FETCH_CHATBOT_TOOLS,
                dispatch,
                data: { chatbotId }
            });
            
            // Assuming the API returns { items: [], total: number }
            return {
                items: result.body,
            };
        },

        // Create tool
        createChatbotTool: (projectId, chatbotId, body) => async (dispatch) => {
            let result = await post({
                host: Hosts.EDGE_CLOUD_CONTROLLER_API,
                url: `/chatbot/${chatbotId}/tools`,
                headers: authHeaders(),
                action: CREATE_CHATBOT_TOOL,
                dispatch,
                body: body,
                data: { chatbotId }
            });
            return result.body;
        },

        // Get single tool
        getChatbotTool: (projectId, chatbotId, toolId) => async (dispatch) => {
            let result = await get({
                host: Hosts.EDGE_CLOUD_CONTROLLER_API,
                url: `/chatbot/${chatbotId}/tools/${toolId}`,
                params: { project_id: projectId },
                headers: authHeaders(),
                action: FETCH_CHATBOT_TOOL,
                dispatch,
                data: { chatbotId }
            });
            return result.body;
        },

        // Update tool
        updateChatbotTool: (projectId, chatbotId, toolId, body) => async (dispatch) => {
            let result = await put({
                host: Hosts.EDGE_CLOUD_CONTROLLER_API,
                url: `/chatbot/${chatbotId}/tools/${toolId}`,
                headers: authHeaders(),
                action: UPDATE_CHATBOT_TOOL,
                dispatch,
                body: body,
                data: { chatbotId }
            });
            return result.body;
        },

        // Delete tool
        deleteChatbotTool: (projectId, chatbotId, toolId) => async (dispatch) => {
            let result = await del({
                host: Hosts.EDGE_CLOUD_CONTROLLER_API,
                url: `/chatbot/${chatbotId}/tools/${toolId}`,
                params: { project_id: projectId },
                headers: authHeaders(),
                action: DELETE_CHATBOT_TOOL,
                dispatch,
                data: { chatbotId, toolId }
            });
            return result.body;
        }
    },

    spec: {
        chatbotTools: {}
    },

    modelReducer: (state, type, body, action) => {
        if (action.url && action.result !== RequestState.SUCCESS)
            return state;

        if (type === FETCH_CHATBOT_TOOLS) {
            const { chatbotId } = action.data;
            const { skip = 0 } = action.params;
            
            return {
                ...state,
                chatbotTools: {
                    ...state.chatbotTools,
                    [chatbotId]: skip === 0 
                        ? body 
                        : [...(state.chatbotTools[chatbotId] || []), ...body]
                }
            }
        }

        if (type === CREATE_CHATBOT_TOOL) {
            const { chatbotId } = action.data;
            return {
                ...state,
                chatbotTools: {
                    ...state.chatbotTools,
                    [chatbotId]: [
                        body,
                        ...(state.chatbotTools[chatbotId] || [])
                    ]
                }
            }
        }

        if (type === UPDATE_CHATBOT_TOOL) {
            const { chatbotId } = action.data;
            return {
                ...state,
                chatbotTools: {
                    ...state.chatbotTools,
                    [chatbotId]: (state.chatbotTools[chatbotId] || []).map(tool => 
                        tool.id === body.id ? body : tool
                    )
                }
            }
        }

        if (type === DELETE_CHATBOT_TOOL) {
            const { chatbotId, toolId } = action.data;
            return {
                ...state,
                chatbotTools: {
                    ...state.chatbotTools,
                    [chatbotId]: (state.chatbotTools[chatbotId] || []).filter(tool => 
                        tool.id !== toolId
                    )
                }
            }
        }

        return state;
    }
}

export default ChatbotTools;
